import { Component, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { PanelTitleService } from 'src/app/services/panel-title.service';

@Component({
  selector: 'app-submenu-panel',
  templateUrl: './submenu-panel.component.html',
  styleUrls: ['./submenu-panel.component.scss'],
})
export class SubmenuPanelComponent implements OnInit, OnDestroy {
  itemActive: any = 1;

  submenus: any[] = [
    {
      route: '/list-business',
      name: 'Empresas',
      url: '/list-business',
      requiredCitylok: true,
      items: [
        { name: 'Listado de empresas', url: '/list-business', exact: true },
      ],
    },
    {
      route: '/events',
      name: 'Eventos',
      url: '/events',
      items: [
        { name: 'Lista de eventos', url: '/events/list', exact: true },
        {
          name: 'Borradores',
          url: '/events/draft',
          exact: true,
          requiredCreator: true,
        },
      ],
    },
    {
      route: '/orders',
      name: 'Pedidos',
      url: '/orders',
      requiredOrders: true,
      items: [{ name: 'Lista de pedidos', url: '/orders', exact: true }],
    },
    {
      route: '/subscribers',
      name: 'Usuarios',
      url: '/subscribers',
      items: [
        {
          name: 'Listado de usuarios',
          url: '/subscribers/list-users',
          exact: true,
          requiredMarketing: true,
        },
        {
          name: 'Miembros',
          url: '/subscribers/list-subscribers',
          exact: false,
          requiredMarketing: true,
          requiredSpace: true,
        },
      ],
    },
    {
      route: '/reports',
      name: 'Informes',
      url: '/reports',
      requiredOrders: true,
      items: [{ name: 'Descarga de informes', url: '/reports', exact: true }],
    },
    {
      route: '/discounts',
      name: 'Descuentos',
      url: '/discounts',
      requiredOrders: true,
      items: [
        { name: 'Lista de descuentos', url: '/discounts', exact: true },
      ],
    },
    {
      route: '/bonds',
      name: 'Abonos',
      url: '/bonds',
      requiredSales: true,
      items: [
        { name: 'Lista de abonos', url: '/bonds', exact: true },
      ],
    },
    {
      route: '/sports-passes',
      name: 'Abonos de temporada',
      url: '/sports-passes',
      requiredSales: true,
      items: [
        { name: 'Abonos', url: '/sports-passes/passes', exact: false },
        // DESACTIVADO 31-08-2026: renovación de los abonados migrados del
        // sistema anterior. Sus componentes y rutas siguen en el repo,
        // comentados (ver 'sports-passes/renewals' en app-routing).
        // { name: 'Renovaciones', url: '/sports-passes/renewals', exact: false },
        { name: 'Pedidos', url: '/sports-passes/orders', exact: false },
        { name: 'Temporadas', url: '/sports-passes/seasons', exact: false },
        { name: 'Productos', url: '/sports-passes/products', exact: false },
      ],
    },
  ];

  private MEMBER_ALLOWED_FUNCTIONS = [
    'marketing',
    'gestor de eventos',
    'soporte',
  ];

  submenuActive: any = null;

  profile = this.auth.getProfile();
  rolesProfile: any[] = this.profile?.roles?.map((r: any) => r.name) || [];

  superadmin = false;
  admin = false;
  owner = false;
  allowOrder = false;
  allowSales = false;
  allowMarketing = false;
  allowCreator = false;
  allowCitylok = false;
  spaceSelected = false;

  businessStorage = this.apiBusiness.getBusinessStorage();

  private placeSubscription!: Subscription;
  private routerSubscription!: Subscription;

  constructor(
    private router: Router,
    private auth: AuthenticationService,
    private apiBusiness: ApiBusinessService,
    public panelTitleService: PanelTitleService,
  ) {}

  ngOnInit(): void {
    this.placeSubscription = this.apiBusiness.placeStorage.subscribe(
      (selectedPlace) => {
        this.calcularPermisos(selectedPlace);
      },
    );

    this.submenuActive = this.findSubmenuByUrl(this.router.url);

    this.routerSubscription = this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.submenuActive = this.findSubmenuByUrl(event.url);
      }
    });
  }

  /**
   * La ruta se compara como segmento completo bajo /panel-control para que una
   * subruta no active otro submenú (p.ej. /sports-passes/orders vs /orders).
   */
  private findSubmenuByUrl(url: string): any {
    const path = url.split(/[?#]/)[0];

    return (
      this.submenus.find((v) => {
        const base = `/panel-control${v.route}`;
        return path === base || path.startsWith(`${base}/`);
      }) ?? null
    );
  }

  private calcularPermisos(selectedPlace: any): void {
    this.profile = this.auth.getProfile();
    this.rolesProfile = this.profile?.roles?.map((r: any) => r.name) || [];

    this.spaceSelected = !!selectedPlace;

    // RESET
    this.superadmin = false;
    this.admin = false;
    this.allowOrder = false;
    this.allowSales = false;
    this.allowMarketing = false;
    this.allowCreator = false;
    this.allowCitylok = false;

    let spaceFunctions: string[] = [];

    this.owner = this.businessStorage?.id_user === this.profile?.id;

    // Buscar funciones del space
    if (selectedPlace && this.profile?.companies) {
      // ⬅️ soporta objeto, number o string
      const selectedPlaceId =
        typeof selectedPlace === 'object'
          ? Number(selectedPlace.id)
          : Number(selectedPlace);

      this.profile.companies.forEach((company: any) => {
        const currentSpace = company.spaces?.find(
          (s: any) => Number(s.id) === selectedPlaceId,
        );

        if (currentSpace?.functions) {
          spaceFunctions = currentSpace.functions.map((f: any) =>
            this.normalize(f.name),
          );
        }
      });
    }

    // =====================================
    // 🔥 FIX IMPORTANTE (ADMIN != SUPERADMIN)
    // =====================================
    if (this.rolesProfile.includes('superadmin')) {
      this.superadmin = true;
      this.admin = true;
      this.owner = true;
      this.allowCitylok = true;
      this.allowCreator = true;
      this.allowSales = true;
      this.allowOrder = true;
      this.allowMarketing = true;
    } else if (this.rolesProfile.includes('admin')) {
      this.admin = true;
      // NO convertir en superadmin
    }

    // permisos por funciones del space
    if (spaceFunctions.length > 0 && !this.superadmin) {
      if (spaceFunctions.includes('Administración')) this.admin = true;
      if (spaceFunctions.includes('Gestor de eventos'))
        this.allowCreator = true;
      if (spaceFunctions.includes('Ventas')) this.allowSales = true;
      if (spaceFunctions.includes('Marketing')) this.allowMarketing = true;
      if (spaceFunctions.includes('Soporte')) this.allowOrder = true;
      if (spaceFunctions.includes('Finanzas')) this.allowOrder = true;
      if (spaceFunctions.includes('QR APP')) this.allowCitylok = true;
    }

    // =====================================
    // REGLA FINAL TAB MIEMBROS
    // =====================================
    const canMembers =
      this.superadmin ||
      this.MEMBER_ALLOWED_FUNCTIONS.some((fn) => spaceFunctions.includes(fn));

    this.setDisabledStatus('Miembros', !this.spaceSelected || !canMembers);

    this.setDisabledStatus('Listado de usuarios', false);
    this.verificarYRedirigirSiNoTienePermiso();
  }

  private setDisabledStatus(itemName: string, disabled: boolean) {
    this.submenus.forEach((menu) => {
      const item = menu.items.find((i: any) => i.name === itemName);
      if (item) {
        item.disabled = disabled;
      }
    });
  }

  private normalize(value: string): string {
    return (value || '')
      .normalize('NFD') // separa acentos
      .replace(/[\u0300-\u036f]/g, '') // elimina tildes
      .trim()
      .toLowerCase();
  }

  private verificarYRedirigirSiNoTienePermiso(): void {
    const urlActual = this.router.url;

    // ¿Está dentro de Miembros?
    const estaEnMiembros = urlActual.includes('/subscribers/list-subscribers');

    if (!estaEnMiembros) return;

    // buscamos el estado actual del item
    const menuUsuarios = this.submenus.find((m) => m.route === '/subscribers');

    const itemMiembros = menuUsuarios?.items.find(
      (i: any) => i.name === 'Miembros',
    );

    // si quedó deshabilitado → redirigir
    if (itemMiembros?.disabled) {
      this.router.navigate(['/panel-control/subscribers/list-users']);
    }
  }

  ngOnDestroy(): void {
    this.placeSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }
}
